import React, { useState } from 'react';
import {
  FaLock,
  FaComments,
  FaShieldAlt,
  FaStar,
  FaBalanceScale,
  FaUsers,
  FaPlus,
  FaMinus,
} from 'react-icons/fa';

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: 'Can I find out who commented on me?',
      a: 'No, identities remain confidential to protect privacy. This ensures honest, unbiased feedback without fear of retaliation.',
    },
    {
      q: 'How is my data protected?',
      a: 'We use encryption and strict access controls. All data is stored securely and complies with leading privacy standards.',
    },
    {
      q: 'Can I delete or edit comments?',
      a: 'Users can manage their own comments. Comments made by others remain immutable to maintain integrity and accountability.',
    },
    {
      q: 'What happens if someone posts inappropriate content?',
      a: 'We have automated and human moderation systems that filter out inappropriate, false, or harassing content. We maintain internal accountability records to prevent abuse.',
    },
    {
      q: 'Why is anonymity important?',
      a: 'Anonymity encourages candid, unbiased feedback free from social pressure. It protects users while building a culture of trust and openness.',
    },
  ];

  const features = [
    {
      icon: <FaLock className='text-white text-3xl' />,
      title: 'Complete Privacy',
      text: 'Only you can see what others have said about you. No one else can access your personal feedback hub.',
    },
    {
      icon: <FaComments className='text-white text-3xl' />,
      title: 'Anonymous Engagement',
      text: "Comment on others' reviews while your identity remains confidential, fostering honest dialogue.",
    },
    {
      icon: <FaShieldAlt className='text-white text-3xl' />,
      title: 'Secure Storage',
      text: "All reviews are stored with encrypted identity data that's never displayed publicly or to other users.",
    },
    {
      icon: <FaStar className='text-white text-3xl' />,
      title: 'Honest Feedback',
      text: 'Encourages candid, unbiased feedback free from social pressure or fear of retaliation.',
    },
    {
      icon: <FaBalanceScale className='text-white text-3xl' />,
      title: 'Fair & Transparent',
      text: 'No review gating or selective display. All genuine comments are treated equally.',
    },
    {
      icon: <FaUsers className='text-white text-3xl' />,
      title: 'Accountability Maintained',
      text: 'While identities are hidden, we log who said what to prevent abuse and maintain integrity.',
    },
  ];

  const steps = [
    { n: 1, title: 'Sign Up', text: 'Create your secure account in seconds' },
    {
      n: 2,
      title: 'View Feedback',
      text: 'Access your personal dashboard to see all comments about you',
    },
    {
      n: 3,
      title: 'Engage Anonymously',
      text: "Comment on others' reviews while staying anonymous",
    },
    {
      n: 4,
      title: 'Grow Together',
      text: 'Build trust and receive honest feedback that empowers growth',
    },
  ];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className='min-h-screen bg-darkBg text-white overflow-hidden selection:bg-white selection:text-black'>
      {/* Hero */}
      <section className='relative h-[90vh] flex flex-col justify-center items-center text-center px-6'>
        {/* Clean black background, no gradients */}
        <div className='absolute inset-0 bg-darkBg'></div>

        <h1 className='text-5xl md:text-7xl font-extrabold tracking-tight mb-8 relative z-10 animate-slideUp'>
          Your Voice, <br className="md:hidden" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">Your Privacy</span>
        </h1>
        <p className='text-gray-400 max-w-2xl mb-12 relative z-10 text-lg md:text-xl animate-slideUp [animation-delay:200ms] opacity-0'>
          Experience a new way to share and receive feedback in a secure,
          private environment.
        </p>
        <div className='flex flex-col sm:flex-row gap-6 relative z-10 animate-slideUp [animation-delay:400ms] opacity-0'>
          <button
            onClick={() => scrollToId('signup')}
            className='px-10 py-4 bg-white text-black rounded-full font-bold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]'
          >
            Get Started
          </button>
          <button
            onClick={() => scrollToId('how-it-works')}
            className='px-10 py-4 bg-transparent text-white rounded-full border border-white/20 hover:bg-white/10 transition backdrop-blur-sm font-semibold'
          >
            Learn More
          </button>
        </div>
      </section>

      {/* Features */}
      <section
        id='features'
        className='relative py-32 bg-black text-white overflow-hidden'
      >
        <div className='relative z-10 max-w-6xl mx-auto px-6'>
          <h2 className='text-4xl md:text-5xl font-bold text-center mb-24 text-white'>
            Core Principles of Privacy
          </h2>

          <div className='grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'>
            {features.map((f, i) => (
              <div
                key={i}
                className='relative group p-8 rounded-xl bg-black overflow-hidden transition-all duration-500 hover:-translate-y-3'
              >
                {/* Animated gradient border */}
                <div className='absolute inset-0 rounded-xl bg-gradient-to-r from-white via-gray-400 to-white opacity-20 group-hover:opacity-100 transition-opacity duration-500 p-[1px]'>
                  <div className='w-full h-full bg-black rounded-xl'></div>
                </div>

                {/* Glowing background effect on hover */}
                <div className='absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500'></div>

                <div className='relative z-10 flex flex-col items-center text-center'>
                  <div className='text-4xl mb-6 text-white group-hover:scale-110 transition-transform duration-500'>
                    {f.icon}
                  </div>
                  <h3 className='text-xl font-bold mb-4 text-white group-hover:text-white transition-colors'>
                    {f.title}
                  </h3>
                  <p className='text-gray-400 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300'>
                    {f.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id='how-it-works'
        className='py-32 bg-black text-white border-t border-white/5'
      >
        <div className='max-w-6xl mx-auto px-6 text-center'>
          <h2 className='text-4xl font-bold mb-16'>How It Works</h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10'>
            {steps.map((s) => (
              <div
                key={s.n}
                className='relative group bg-black p-6 rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2'
              >
                {/* Animated border */}
                <div className='absolute inset-0 rounded-xl bg-gradient-to-br from-white via-gray-500 to-white opacity-30 group-hover:opacity-100 transition-opacity duration-500 p-[1px]'>
                  <div className='w-full h-full bg-black rounded-xl'></div>
                </div>

                <div className='relative z-10'>
                  <div className='w-16 h-16 bg-white text-black rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-[0_0_20px_rgba(255,255,255,0.4)] group-hover:shadow-[0_0_30px_rgba(255,255,255,0.6)] transition-all duration-500 group-hover:scale-110'>
                    {s.n}
                  </div>
                  <h3 className='text-xl font-bold mb-3'>{s.title}</h3>
                  <p className='text-gray-400 text-sm group-hover:text-gray-200 transition-colors'>{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id='faq' className='py-32 bg-black border-t border-white/5'>
        <div className='max-w-4xl mx-auto px-6'>
          <h2 className='text-4xl font-bold text-center mb-16'>FAQs</h2>
          <div className='space-y-5'>
            {faqs.map((f, i) => {
              const active = openFaq === i;
              return (
                <div
                  key={i}
                  className='relative group overflow-hidden rounded-xl transition-all duration-300'
                >
                  {/* Animated border */}
                  <div className={`absolute inset-0 rounded-xl transition-all duration-500 p-[1px] ${active ? 'bg-gradient-to-r from-white via-gray-400 to-white opacity-100' : 'bg-white opacity-20 group-hover:opacity-60'
                    }`}>
                    <div className='w-full h-full bg-black rounded-xl'></div>
                  </div>

                  {/* Glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-white/10 to-transparent transition-opacity duration-500 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                    }`}></div>

                  <div className='relative z-10'>
                    <button
                      onClick={() => setOpenFaq(active ? null : i)}
                      className='w-full flex justify-between items-center px-8 py-7 font-semibold text-left text-lg transition-all duration-300 group-hover:px-10'
                    >
                      <span className='pr-4'>{f.q}</span>
                      <span className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-500 ${active ? 'rotate-180 bg-white/10' : 'rotate-0 group-hover:bg-white/5'
                        }`}>
                        {active ? <FaMinus className="text-xs text-white" /> : <FaPlus className="text-xs text-gray-400 group-hover:text-white" />}
                      </span>
                    </button>
                    {active && (
                      <div className='px-8 pb-7 text-gray-400 text-base leading-relaxed animate-slideUp'>
                        {f.a}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id='signup'
        className='py-32 text-center bg-black text-white border-t border-white/5'
      >
        <h2 className='text-4xl md:text-5xl font-bold mb-6'>Join the Future of Feedback</h2>
        <p className='text-gray-400 mb-10 max-w-2xl mx-auto text-lg'>
          Experience feedback that empowers growth without compromising privacy.
        </p>
        <button className='bg-white text-black font-bold px-12 py-5 rounded-full hover:scale-105 transition-transform shadow-[0_0_25px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)]'>
          Create Account
        </button>
      </section>
    </main>
  );
}
